// src/components/UserList.jsx
import React, { useState, useMemo } from "react";
import Button1 from "@material-ui/core/Button";

function getInitials(user) {
  const f = user.USER_FIRSTNAME?.[0] ?? "";
  const l = user.USER_LASTNAME?.[0]  ?? "";
  return (f + l).toUpperCase() || "?";
}

// สี avatar วนตาม CRM_UID
const AVATAR_COLORS = [
  { bg: "#eff6ff", text: "#1d4ed8" },
  { bg: "#f0fdf4", text: "#15803d" },
  { bg: "#fdf4ff", text: "#7e22ce" },
  { bg: "#fff7ed", text: "#c2410c" },
  { bg: "#f0fdfa", text: "#0f766e" },
];
function avatarColor(uid) {
  return AVATAR_COLORS[uid % AVATAR_COLORS.length];
}

export function UserList({ users, selectedId, onSelect, onAdd }) {
  const [search,       setSearch]       = useState("");
  const [filterActive, setFilterActive] = useState("all"); // "all" | "1" | "0"

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return users.filter(u => {
      const matchSearch = !q || [
        u.USER_FIRSTNAME, u.USER_LASTNAME, u.USER_EMAIL,
        u.USER_NICKNAME,  u.EMP_CODE,      u.USER_POSITION,
      ].some(v => v?.toLowerCase().includes(q));

      const matchActive =
        filterActive === "all"       ? true :
        filterActive === "1"         ? u.USER_ACTIVE :
        /* filterActive === "0" */     !u.USER_ACTIVE;

      return matchSearch && matchActive;
    });
  }, [users, search, filterActive]);

  return (
    <div style={{
      width:         300,
      flexShrink:    0,
      borderRight:   "1px solid #e2e8f0",
      display:       "flex",
      flexDirection: "column",
      background:    "#fff",
      height:        "100%",
    }}>
      {/* Header */}
      <div style={{
        padding:        "12px 14px",
        borderBottom:   "1px solid #e2e8f0",
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
      }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
          All Users
          <span style={{
            marginLeft:   6, fontSize: 11, fontWeight: 600,
            background:   "#f1f5f9", color: "#64748b",
            padding:      "1px 6px", borderRadius: 10,
          }}>{filtered.length}</span>
        </span>
        <Button1
          variant="outlined" size="small"
          onClick={() => onAdd()}
          style={{ fontSize: 12, textTransform: "none", padding: "2px 10px" }}
        >
          + Add
        </Button1>
      </div>

      {/* Search */}
      <div style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>
        <input
          type="text"
          placeholder="Search name, email, emp code..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: "100%", padding: "6px 10px", fontSize: 12,
            border: "1px solid #e2e8f0", borderRadius: 6,
            color: "#1e293b", outline: "none",
          }}
        />
      </div>

      {/* Filter active */}
      <div style={{
        padding:     "8px 12px",
        borderBottom: "1px solid #e2e8f0",
        display:     "flex", gap: 6,
      }}>
        {[["all", "All"], ["1", "Active"], ["0", "Inactive"]].map(([val, label]) => (
          <span
            key={val}
            onClick={() => setFilterActive(val)}
            style={{
              fontSize:     11, fontWeight:  600,
              padding:      "3px 10px", borderRadius: 10,
              cursor:       "pointer", userSelect: "none",
              background:   filterActive === val ? "#1d4ed8"  : "#f1f5f9",
              color:        filterActive === val ? "#fff"      : "#64748b",
              border:       filterActive === val ? "1px solid #1d4ed8" : "1px solid #e2e8f0",
              transition:   "all .12s",
            }}
          >{label}</span>
        ))}
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
        {filtered.length === 0 ? (
          <p style={{ padding: "16px", fontSize: 13, color: "#94a3b8" }}>No users found.</p>
        ) : filtered.map(u => {
          const isActive = selectedId === u.CRM_UID;
          const av       = avatarColor(u.CRM_UID);
          return (
            <div
              key={u.CRM_UID}
              onClick={() => onSelect(u.CRM_UID)}
              style={{
                display:     "flex", alignItems: "center", gap: 10,
                padding:     "8px 14px", cursor: "pointer",
                background:  isActive ? "#eff6ff" : "transparent",
                borderLeft:  isActive ? "3px solid #1d4ed8" : "3px solid transparent",
                transition:  "background 0.1s",
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#f8fafc"; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
            >
              {/* Avatar */}
              <div style={{
                width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                background: av.bg, color: av.text,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700,
              }}>
                {getInitials(u)}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: isActive ? "#1d4ed8" : "#1e293b",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {u.USER_FIRSTNAME} {u.USER_LASTNAME}
                  {u.USER_NICKNAME ? <span style={{ fontWeight: 400, color: "#64748b" }}> ({u.USER_NICKNAME})</span> : null}
                </div>
                <div style={{
                  fontSize: 11, color: "#64748b",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {u.USER_EMAIL}
                </div>
              </div>

              {/* Active badge */}
              <span style={{
                fontSize: 10, padding: "2px 6px", borderRadius: 10, fontWeight: 700, flexShrink: 0,
                background: u.USER_ACTIVE ? "#dcfce7" : "#f1f5f9",
                color:      u.USER_ACTIVE ? "#16a34a" : "#94a3b8",
              }}>
                {u.USER_ACTIVE ? "On" : "Off"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
